import { Controller } from "@hotwired/stimulus"

// Controller for field validation on blur using fetch API
export default class extends Controller {
  static values = {
    environmentId: String,
    mappingId: String
  }

  static targets = ["results"]

  async validateField(event) {
    const fieldValue = event.target.value
    const fieldName = event.target.name

    // Only validate if we have meaningful input
    if (fieldValue.length < 2) return

    // Ensure we have the environment ID before attempting validation
    if (!this.environmentIdValue) {
      console.warn('Field validator: environmentId not set, skipping validation')
      return
    }

    await this.fetchValidation(fieldName, fieldValue)
  }

  async fetchValidation(fieldName, fieldValue) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    const url = `/admin/salesforce/environments/${this.environmentIdValue}/field-mappings/validate_mapping`

    const params = new URLSearchParams()
    // Extract the actual field name from the form field name (e.g., "salesforce_field_mapping[salesforce_field]" -> "salesforce_field")
    const actualFieldName = fieldName.match(/\[(\w+)\]$/)?.[1] || fieldName
    params.append(actualFieldName, fieldValue)
    if (this.mappingIdValue) {
      params.append('id', this.mappingIdValue)
    }

    try {
      const response = await fetch(`${url}?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken,
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (!response.ok) {
        console.error('Validation request failed:', response.status)
        return
      }

      const result = await response.json()
      this.displayValidationResult(result)
    } catch (error) {
      console.error('Validation error:', error)
    }
  }

  displayValidationResult(result) {
    const resultsContainer = document.getElementById('validation_results')
    if (!resultsContainer) return

    if (result.valid) {
      resultsContainer.innerHTML = result.errors?.length ? '' : `
        <div class="mt-2 text-sm text-green-400">
          <span class="flex items-center gap-1">
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
            </svg>
            Field name is valid
          </span>
        </div>
      `
    } else {
      const errorsHtml = result.errors?.map(e => `<li>${e}</li>`).join('') || ''
      const warningsHtml = result.warnings?.map(w => `<li>${w}</li>`).join('') || ''

      resultsContainer.innerHTML = `
        ${errorsHtml ? `<div class="mt-2 text-sm text-red-400"><ul class="list-disc pl-4">${errorsHtml}</ul></div>` : ''}
        ${warningsHtml ? `<div class="mt-2 text-sm text-yellow-400"><ul class="list-disc pl-4">${warningsHtml}</ul></div>` : ''}
      `
    }

    // Show suggestions if available
    if (result.suggestions?.length) {
      const suggestionsHtml = result.suggestions.map(s => `<li>${s}</li>`).join('')
      resultsContainer.innerHTML += `
        <div class="mt-2 text-sm text-blue-400">
          <strong>Suggestions:</strong>
          <ul class="list-disc pl-4">${suggestionsHtml}</ul>
        </div>
      `
    }
  }
}